import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ChevronLeft, ChevronRight, Clock, User, BookOpen,
  FileText, CheckCircle, Award, Code, Edit3, Save,
  RotateCcw, Download, Bot, UserCheck, AlertCircle,
  Users, ClipboardCheck
} from 'lucide-react';
import { supabase, getSupabaseUrl } from '../../supabase/client';
import { formatDateTime } from '../../utils/timeUtils';
import Editor from '@monaco-editor/react';
import QuestionHtmlWithZoom from '../../components/QuestionHtmlWithZoom';
import { getImageUrl, processQuestionHtml } from '../../utils/questionHtml';

// ==================== 类型定义 ====================

interface Question {
  id: string;
  content: string;
  title?: string;
  name?: string; // 题目名称（管理用），如"芝麻开门"（旧字段，兼容）
  questionName?: string; // 题目名称（新字段，优先使用）
  question_type: string;
  score: number;
  options?: any[];
  correct_answer?: string;
  reference_answer?: string;
  scoring_points?: string;
  explanation?: string;
  title_images?: string[];
  title_image_widths?: number[];
  image_position?: string; // 图片位置：above/below
  title_image_layout?: string; // 图片布局：single/multi
}

interface StudentAnswer {
  question_id: string;
  answer: string;
  answer_text?: string;
  code_content?: string;
  file_url?: string;
  file_name?: string;
}

interface ExamRecord {
  id: string;
  user_id: string;
  paper_id: string;
  paper_title: string;
  username: string;
  answers: Record<string, StudentAnswer> | null;
  questions: any[] | null;
  score: number | null;
  total_score: number;
  grading_status: string;
  ai_score?: number | null;
  ai_feedback?: string | null;
  teacher_score?: number | null;
  teacher_feedback?: string | null;
  question_scores?: Record<string, number> | null;
  question_feedbacks?: Record<string, { score?: number; feedback?: string } | string> | null;
  objective_score?: number | null;
  programming_score?: number | null;
  created_at: string;
  graded_at?: string | null;
}

interface GradingDraft {
  score: number;
  feedback: string;
  savedAt: string;
}

interface StudentInfo {
  student_id: string;
  username: string;
  status: 'pending' | 'completed' | 'expired';
  score: number | null;
  completed_at: string | null;
  grading_status?: string;
  graded_coding_count?: number;
  paper_coding_count?: number;
}

// ==================== 辅助函数 ====================

const getLanguage = (questionType: string) => {
  if (questionType === 'python_coding') return 'python';
  if (questionType === 'cpp_coding') return 'cpp';
  return 'javascript';
};

const isCodeQuestion = (questionType: string) => {
  return ['python_coding', 'cpp_coding', 'coding'].includes(questionType);
};

const isScratchQuestion = (questionType: string) => {
  return questionType === 'scratch_coding' || questionType === 'scratch';
};

const isObjectiveQuestion = (questionType: string) => {
  return ['single_choice', 'multi_choice', 'judge'].includes(questionType);
};

const getQuestionTypeLabel = (type: string) => {
  const labels: Record<string, string> = {
    'single_choice': '单选题',
    'multi_choice': '多选题',
    'judge': '判断题',
    'fill_blank': '填空题',
    'short_answer': '简答题',
    'coding': '编程题',
    'python_coding': 'Python编程',
    'cpp_coding': 'C++编程',
    'scratch_coding': '图形化编程'
  };
  return labels[type] || type;
};

// ==================== 主组件 ====================

export default function AssignmentGradingPage() {
  const navigate = useNavigate();
  const { assignmentId, studentId } = useParams<{ assignmentId: string; studentId?: string }>();
  const [searchParams] = useSearchParams();
  const questionIndex = parseInt(searchParams.get('q') || '0');

  // 状态
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [teacherId, setTeacherId] = useState<string | null>(null);

  // 作业数据
  const [assignment, setAssignment] = useState<any>(null);
  const [students, setStudents] = useState<StudentInfo[]>([]);

  // 阅卷数据（仅 studentId 存在时使用）
  const [examRecord, setExamRecord] = useState<ExamRecord | null>(null);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(questionIndex);
  const [studentAnswers, setStudentAnswers] = useState<Record<string, StudentAnswer>>({});

  // 待阅卷学员列表（用于下拉框）
  const [pendingStudents, setPendingStudents] = useState<{user_id: string; username: string; created_at: string}[]>([]);

  // 评分表单
  const [editForm, setEditForm] = useState<Record<string, { score: number; feedback: string }>>({});

  // 草稿自动保存
  const [draftSaved, setDraftSaved] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState<string | null>(null);

  // 当前题目是否已保存（用于控制确认评分按钮状态）
  const [isCurrentQuestionSaved, setIsCurrentQuestionSaved] = useState(false);

  // 初始化
  useEffect(() => {
    loadTeacherInfo();
  }, []);

  useEffect(() => {
    if (teacherId && assignmentId) {
      if (studentId) {
        loadGradingData();
      } else {
        loadStudentList();
      }
    }
  }, [teacherId, assignmentId, studentId]);

  // 自动保存草稿
  useEffect(() => {
    if (!studentId || !examRecord) return;
    const interval = setInterval(() => {
      autoSaveDraft();
    }, 30000);
    return () => clearInterval(interval);
  }, [editForm, currentQuestionIndex]);

  const loadTeacherInfo = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (user) setTeacherId(user.id);
  };

  // ==================== 学员列表模式 ====================

  const loadStudentList = async () => {
    setLoading(true);
    try {
      // 加载作业信息
      const { data: assignmentData } = await supabase
        .from('teacher_assignments')
        .select('*')
        .eq('id', assignmentId)
        .single();
      if (assignmentData) setAssignment(assignmentData);

      // 加载学员列表
      const session = (await supabase.auth.getSession()).data.session;
      if (!session) return;

      const response = await fetch(`${getSupabaseUrl()}/functions/v1/teacher-service`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          action: 'get_assignment_stats',
          assignmentId,
        }),
      });

      const result = await response.json();
      if (result.success) {
        setAssignment(result.data.assignment);
        setStudents(result.data.students || []);
      }
    } catch (error) {
      console.error('加载学员列表失败:', error);
    } finally {
      setLoading(false);
    }
  };

  // ==================== 阅卷模式 ====================

  const loadGradingData = async () => {
    setLoading(true);
    try {
      // 加载作业信息
      const { data: assignmentData } = await supabase
        .from('teacher_assignments')
        .select('*')
        .eq('id', assignmentId)
        .single();
      if (assignmentData) setAssignment(assignmentData);

      // 加载答题记录
      await loadExamRecord();
    } catch (error) {
      console.error('加载阅卷数据失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadExamRecord = async () => {
    if (!studentId || !assignment?.paper_id) return;

    // 获取学员的答题记录（加上 paper_id 条件，避免查到其他试卷的记录）
    const { data: record, error: recordError } = await supabase
      .from('exam_records')
      .select('*')
      .eq('user_id', studentId)
      .eq('paper_id', assignment.paper_id)
      .order('created_at', { ascending: false })
      .limit(1);

    if (recordError || !record || record.length === 0) {
      console.error('未找到答题记录:', recordError);
      return;
    }

    const examData = record[0];

    // 获取学员信息
    const { data: profile } = await supabase
      .from('profiles')
      .select('username')
      .eq('id', studentId)
      .single();

    // 获取试卷信息（先查 papers 表，若不存在则查 teacher_papers 表）
    let paper: any = null;
    const { data: paperFromPapers } = await supabase
      .from('papers')
      .select('title, total_score')
      .eq('id', examData.paper_id)
      .maybeSingle();
    if (paperFromPapers) {
      paper = paperFromPapers;
    } else {
      const { data: paperFromTeacher } = await supabase
        .from('teacher_papers')
        .select('title, total_score')
        .eq('id', examData.paper_id)
        .maybeSingle();
      paper = paperFromTeacher;
    }

    const examRecordData: ExamRecord = {
      id: examData.id,
      user_id: examData.user_id,
      paper_id: examData.paper_id,
      paper_title: paper?.title || '未知试卷',
      username: profile?.username || '未知学员',
      answers: (examData.answers as unknown as Record<string, StudentAnswer>) || null,
      questions: (examData.questions as unknown as any[]) || null,
      score: examData.score,
      total_score: paper?.total_score || examData.total_score || 0,
      grading_status: examData.grading_status,
      ai_score: examData.ai_score,
      ai_feedback: examData.ai_feedback,
      teacher_score: examData.teacher_score,
      teacher_feedback: examData.teacher_feedback,
      question_scores: (examData.question_scores as unknown as Record<string, number>) || null,
      question_feedbacks: (examData.question_feedbacks as unknown as Record<string, { score?: number; feedback?: string } | string>) || null,
      created_at: examData.created_at,
      graded_at: examData.graded_at
    };

    setExamRecord(examRecordData);

    const answers = (examData.answers as unknown as Record<string, StudentAnswer>) || {};
    setStudentAnswers(answers);

    // 加载题目（使用 examData.paper_id）— 先加载题目，再初始化评分表单
    const loadedQuestions = await loadQuestions(examData.paper_id);

    // 初始化评分表单 — 遍历所有题目（包括未作答的），而非仅 answers 的 key
    const initialForm: Record<string, { score: number; feedback: string }> = {};
    const questionScores = (examData.question_scores as unknown as Record<string, number>) || {};
    const questionFeedbacks = (examData.question_feedbacks as unknown as Record<string, { score?: number; feedback?: string } | string>) || {};
    loadedQuestions.forEach(q => {
      const qf = questionFeedbacks[q.id];
      // 兼容新旧格式：新格式为 {score, feedback} 对象，旧格式为纯字符串
      let feedbackStr = '';
      let feedbackScore: number | undefined = undefined;
      if (qf && typeof qf === 'object') {
        feedbackStr = (qf as { score?: number; feedback?: string }).feedback || '';
        feedbackScore = (qf as { score?: number; feedback?: string }).score ?? undefined;
      } else if (qf) {
        feedbackStr = String(qf);
      }
      initialForm[q.id] = {
        score: feedbackScore ?? questionScores[q.id] ?? 0,
        feedback: feedbackStr || ''
      };
    });
    setEditForm(initialForm);

    // 加载本地草稿（传入已加载的题目）
    loadDraft(examData.id, loadedQuestions);

    // 加载该试卷所有待阅卷学员（用于下拉框）
    await loadPendingStudents(examData.paper_id);
  };

  // 加载该试卷所有待阅卷学员
  const loadPendingStudents = async (paperId: string) => {
    const { data: records, error } = await supabase
      .from('exam_records')
      .select('user_id, created_at, grading_status')
      .eq('paper_id', paperId)
      .in('grading_status', ['pending', 'ai_graded'])
      .order('created_at', { ascending: true });

    if (error || !records || records.length === 0) {
      setPendingStudents([]);
      return;
    }

    // 获取学员用户名
    const userIds = records.map(r => r.user_id);
    const { data: profiles } = await supabase
      .from('profiles')
      .select('id, username')
      .in('id', userIds);

    const profileMap = new Map(profiles?.map(p => [p.id, p.username]) || []);

    const students = records.map(r => ({
      user_id: r.user_id,
      username: profileMap.get(r.user_id) || '未知学员',
      created_at: r.created_at
    }));

    setPendingStudents(students);
  };

  const loadQuestions = async (paperId: string): Promise<Question[]> => {
    const { data } = await supabase
      .from('questions')
      .select('*')
      .eq('paper_id', paperId)
      .order('order_index', { ascending: true });

    if (data) {
      // 只保留非客观题
      const subjectiveQuestions = data.filter(q => !isObjectiveQuestion(q.question_type)) as Question[];
      setQuestions(subjectiveQuestions);
      return subjectiveQuestions;
    }
    return [];
  };

  // 自动保存草稿到 localStorage
  const autoSaveDraft = useCallback(() => {
    if (!examRecord) return;

    const currentQ = questions[currentQuestionIndex];
    if (!currentQ) return;

    const draft: GradingDraft = {
      score: editForm[currentQ.id]?.score || 0,
      feedback: editForm[currentQ.id]?.feedback || '',
      savedAt: new Date().toISOString()
    };

    const key = `grading_draft_${examRecord.id}_${currentQ.id}`;
    localStorage.setItem(key, JSON.stringify(draft));
    setDraftSaved(true);
    setLastSavedAt(formatDateTime(draft.savedAt));

    setTimeout(() => setDraftSaved(false), 3000);
  }, [examRecord, questions, currentQuestionIndex, editForm]);

  // 加载草稿
  const loadDraft = (recordId: string, questionList: Question[]) => {
    const drafts: Record<string, { score: number; feedback: string }> = {};

    questionList.forEach(q => {
      const key = `grading_draft_${recordId}_${q.id}`;
      const saved = localStorage.getItem(key);
      if (saved) {
        const draft: GradingDraft = JSON.parse(saved);
        drafts[q.id] = { score: draft.score, feedback: draft.feedback };
      }
    });

    if (Object.keys(drafts).length > 0) {
      setEditForm(prev => ({ ...prev, ...drafts }));
    }
  };

  // 保存评分
  const handleSave = async () => {
    if (!examRecord || !teacherId) return;

    const currentQ = questions[currentQuestionIndex];
    if (!currentQ) return;

    setSaving(true);
    try {
      const formData = editForm[currentQ.id] || { score: 0, feedback: '' };

      // 更新该题的分数
      const newQuestionScores = { ...(examRecord.question_scores || {}) };
      newQuestionScores[currentQ.id] = formData.score;

      // 编程题得分：从 question_scores 中提取编程题部分
      const programmingTypes = ['scratch_coding', 'python_coding', 'cpp_coding'];
      const programmingScore = Math.round((examRecord.questions || []).reduce((total: number, q: any) => {
        if (programmingTypes.includes(q.type) && newQuestionScores[q.id] !== undefined) {
          return total + newQuestionScores[q.id];
        }
        return total;
      }, 0));

      // 总分 = 客观题得分 + 编程题得分
      const objectiveScore = examRecord.objective_score ?? 0;
      const totalScore = objectiveScore + programmingScore;

      // 更新题目级评语和评分（存储为 {score, feedback} 对象，便于前端读取教师评分）
      const newQuestionFeedbacks = { ...(examRecord.question_feedbacks || {}) };
      newQuestionFeedbacks[currentQ.id] = { score: formData.score, feedback: formData.feedback };

      // 判断整体阅卷状态：如果所有主观题都已评分，则标记为 teacher_graded
      // 注意：grading_status CHECK 约束只允许 pending/ai_graded/teacher_graded/final
      const allQuestionsGraded = questions.every(q => newQuestionScores[q.id] !== undefined && newQuestionScores[q.id] !== null);
      const newGradingStatus = allQuestionsGraded ? 'teacher_graded' : 'pending';

      // 更新 exam_records
      // 注意：不更新 objective_score，保持提交时的原值（提交时已计算，教师阅卷不应修改）
      const updatePayload = {
        question_scores: newQuestionScores,
        question_feedbacks: newQuestionFeedbacks,
        score: Math.round(totalScore),
        teacher_score: Math.round(totalScore),
        grading_status: newGradingStatus,
        graded_by: teacherId,
        graded_at: new Date().toISOString(),
        programming_score: programmingScore
      };

      const { error, data } = await supabase
        .from('exam_records')
        .update(updatePayload)
        .eq('id', examRecord.id);

      if (error) {
        console.error('Supabase update error 详情:', error.message, error.code, error.details, error.hint);
        throw error;
      }

      // 清除草稿
      const key = `grading_draft_${examRecord.id}_${currentQ.id}`;
      localStorage.removeItem(key);

      // 更新本地状态
      setExamRecord(prev => prev ? {
        ...prev,
        question_scores: newQuestionScores,
        question_feedbacks: newQuestionFeedbacks,
        score: totalScore,
        teacher_score: totalScore,
        grading_status: newGradingStatus
      } : null);

      // 标记当前题目已保存
      setIsCurrentQuestionSaved(true);

      alert('评分保存成功！');
    } catch (error) {
      console.error('保存评分失败:', error);
      alert('保存评分失败，请重试');
    } finally {
      setSaving(false);
    }
  };

  // 切换题目
  const handlePrevQuestion = () => {
    if (currentQuestionIndex > 0) {
      autoSaveDraft();
      setCurrentQuestionIndex(prev => prev - 1);
      setIsCurrentQuestionSaved(false);
      navigate(`/teacher/assignments/grading/${assignmentId}/${studentId}?q=${currentQuestionIndex - 1}`, { replace: true });
    }
  };

  const handleNextQuestion = () => {
    if (currentQuestionIndex < questions.length - 1) {
      autoSaveDraft();
      setCurrentQuestionIndex(prev => prev + 1);
      setIsCurrentQuestionSaved(false);
      navigate(`/teacher/assignments/grading/${assignmentId}/${studentId}?q=${currentQuestionIndex + 1}`, { replace: true });
    }
  };

  // 更新评分表单
  const updateEditForm = (questionId: string, field: 'score' | 'feedback', value: any) => {
    setEditForm(prev => ({
      ...prev,
      [questionId]: {
        ...prev[questionId],
        [field]: value
      }
    }));
    // 评分或评语改变时，重置保存状态
    setIsCurrentQuestionSaved(false);
  };

  // ==================== 渲染 ====================

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  // ==================== 学员选择模式 ====================
  if (!studentId) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="bg-white border-b border-gray-200 sticky top-0 z-50">
          <div className="px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-4">
              <BookOpen className="w-5 h-5 text-purple-600" />
              <span className="font-semibold text-gray-800">{assignment?.paper_title || '作业阅卷'}</span>
            </div>
            <ClipboardCheck className="w-5 h-5 text-purple-600" />
          </div>
        </div>

        <div className="max-w-4xl mx-auto p-6">
          <h2 className="text-lg font-bold text-gray-800 mb-4">选择要阅卷的学员</h2>

          {students.length === 0 ? (
            <div className="bg-white rounded-xl p-12 text-center shadow-sm border border-gray-100">
              <Users className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p className="text-gray-500 mb-4">暂无学员完成作业</p>
              <button
                onClick={() => navigate('/teacher/assignments')}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
              >
                返回作业管理
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {students.map((student) => (
                <motion.div
                  key={student.student_id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                        <User className="w-5 h-5 text-purple-600" />
                      </div>
                      <div>
                        <span className="font-semibold text-gray-800">{student.username}</span>
                        <div className="flex items-center gap-3 mt-1 text-sm text-gray-500">
                          {student.status === 'completed' && (
                            <span className="flex items-center gap-1 text-green-600">
                              <CheckCircle className="w-3 h-3" />
                              已完成
                            </span>
                          )}
                          {student.completed_at && (
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {formatDateTime(student.completed_at)}
                            </span>
                          )}
                          {student.paper_coding_count && student.paper_coding_count > 0 && (
                            <span className="flex items-center gap-1 text-gray-600">
                              <span className="text-xs">
                                {student.graded_coding_count || 0}/{student.paper_coding_count}
                              </span>
                            </span>
                          )}
                          {student.grading_status === 'teacher_graded' &&
                           student.graded_coding_count === student.paper_coding_count && (
                            <span className="flex items-center gap-1 text-blue-600">
                              <UserCheck className="w-3 h-3" />
                              已阅卷
                            </span>
                          )}
                          {student.grading_status === 'ai_graded' && (
                            <span className="flex items-center gap-1 text-amber-600">
                              <Bot className="w-3 h-3" />
                              AI已评
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    {student.status === 'completed' ? (
                      <button
                        onClick={() => navigate(`/teacher/assignments/grading/${assignmentId}/${student.student_id}`)}
                        className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                      >
                        <ClipboardCheck className="w-4 h-4" />
                        开始阅卷
                      </button>
                    ) : (
                      <span className="px-3 py-1.5 text-sm text-gray-400 bg-gray-100 rounded-lg">
                        未完成
                      </span>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ==================== 阅卷模式 ====================

  const currentQuestion = questions[currentQuestionIndex];

  // 获取当前题的学生答案 — answers 中编程题的值可能是 URL 字符串而非 StudentAnswer 对象
  const rawAnswer = currentQuestion ? studentAnswers[currentQuestion.id] : null;
  let currentAnswer: StudentAnswer | null = null;
  let hasAnswer = false;
  if (rawAnswer) {
    hasAnswer = true;
    if (typeof rawAnswer === 'string') {
      // 编程题答案可能是纯 URL 字符串（如 sb3 文件 URL）
      if (isScratchQuestion(currentQuestion?.question_type || '')) {
        currentAnswer = { question_id: currentQuestion!.id, answer: '', file_url: rawAnswer, file_name: 'project.sb3' };
      } else if (isCodeQuestion(currentQuestion?.question_type || '')) {
        currentAnswer = { question_id: currentQuestion!.id, answer: '', code_content: rawAnswer };
      } else {
        currentAnswer = { question_id: currentQuestion!.id, answer: rawAnswer };
      }
    } else if (typeof rawAnswer === 'object') {
      currentAnswer = rawAnswer as StudentAnswer;
    }
  }

  const currentForm = currentQuestion ? editForm[currentQuestion.id] || { score: 0, feedback: '' } : { score: 0, feedback: '' };

  if (!examRecord || questions.length === 0) {
    return (
      <div className="flex items-center justify-center h-screen text-gray-500">
        <div className="text-center">
          <AlertCircle className="w-16 h-16 mx-auto mb-4 opacity-50" />
          <p className="text-lg mb-2">暂无需要阅卷的题目</p>
          <p className="text-sm text-gray-400 mb-4">
            该学员的答题记录中没有主观题，或尚未提交答案
          </p>
          <button
            onClick={() => navigate(`/teacher/assignments/grading/${assignmentId}`)}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
          >
            选择其他学员
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* 顶部信息栏 */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="px-6 py-4">
          {/* 第一行：主要信息 */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <select
                  value={studentId || ''}
                  onChange={(e) => {
                    const selectedStudentId = e.target.value;
                    if (selectedStudentId && selectedStudentId !== studentId) {
                      navigate(`/teacher/assignments/grading/${assignmentId}/${selectedStudentId}?q=${currentQuestionIndex}`, { replace: true });
                    }
                  }}
                  className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg bg-white text-gray-700 focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none cursor-pointer min-w-[200px]"
                >
                  <option value="">选择学员</option>
                  {pendingStudents.map((student) => (
                    <option key={student.user_id} value={student.user_id}>
                      {student.username}
                    </option>
                  ))}
                </select>
              </div>
              <div className="h-6 w-px bg-gray-200"></div>
              <div className="flex items-center gap-2">
                <User className="w-5 h-5 text-purple-600" />
                <span className="font-semibold text-gray-800">{examRecord.username}</span>
              </div>
              <div className="flex items-center gap-2 text-gray-500">
                <Clock className="w-4 h-4" />
                <span className="text-sm">提交于 {formatDateTime(examRecord.created_at)}</span>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <span className="text-gray-500">当前题评卷状态：</span>
              {(() => {
                // 按题目判断评分状态，而非使用记录级的 grading_status
                const qScores = examRecord.question_scores || {};
                const qFeedbacks = examRecord.question_feedbacks || {};
                const currentQId = currentQuestion?.id;
                const hasTeacherScore = currentQId && qScores[currentQId] !== undefined && qScores[currentQId] !== null;
                if (hasTeacherScore) {
                  return (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm">
                      <UserCheck className="w-4 h-4" />
                      已阅卷
                    </span>
                  );
                }
                if (examRecord.grading_status === 'ai_graded') {
                  return (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm">
                      <Bot className="w-4 h-4" />
                      AI已评
                    </span>
                  );
                }
                return (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-sm">
                    <Clock className="w-4 h-4" />
                    待阅卷
                  </span>
                );
              })()}
            </div>
          </div>

          {/* 第二行：试卷信息和题号切换 */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <BookOpen className="w-5 h-5 text-purple-600" />
              <span className="font-medium text-gray-800">{examRecord.paper_title}</span>
              <span className="text-gray-400">|</span>
              <span className="text-gray-600">
                第 <span className="font-semibold text-purple-600">{currentQuestionIndex + 1}</span> / {questions.length} 题
              </span>
              {currentQuestion && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded">
                  {getQuestionTypeLabel(currentQuestion.question_type)}
                </span>
              )}
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={handlePrevQuestion}
                disabled={currentQuestionIndex === 0}
                className="flex items-center gap-1 px-3 py-1.5 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft className="w-4 h-4" />
                上一题
              </button>
              <button
                onClick={handleNextQuestion}
                disabled={currentQuestionIndex === questions.length - 1}
                className="flex items-center gap-1 px-3 py-1.5 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                下一题
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* 左右分栏主体 */}
      <div className="flex h-[calc(100vh-140px)]">
        {/* 左栏 */}
        <div className="w-1/2 border-r border-gray-200 overflow-y-auto bg-white">
          <div className="p-6 space-y-6">
            {/* 题干区 */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-gray-50 rounded-xl border border-gray-200 overflow-hidden"
            >
              <div className="px-4 py-3 bg-gray-100 border-b border-gray-200 flex items-center gap-2">
                <FileText className="w-4 h-4 text-gray-600" />
                <span className="font-medium text-gray-700">
                  {(() => {
                    const isCoding = currentQuestion?.question_type === 'python_coding' ||
                                     currentQuestion?.question_type === 'cpp_coding' ||
                                     currentQuestion?.question_type === 'scratch_coding';
                    if (isCoding) {
                      if (currentQuestion?.questionName ?? currentQuestion?.name) {
                        return currentQuestion?.questionName ?? currentQuestion?.name;
                      }
                      const codingQuestions = questions.filter(q =>
                        q.question_type === 'python_coding' || q.question_type === 'cpp_coding' || q.question_type === 'scratch_coding'
                      );
                      const codingIndex = codingQuestions.findIndex(q => q.id === currentQuestion?.id) + 1;
                      return codingIndex > 0 ? `未命名${codingIndex}` : '题目';
                    }
                    return currentQuestion?.name || currentQuestion?.title || '题目';
                  })()}
                </span>
                <span className="ml-auto text-sm text-purple-600 font-medium">
                  满分 {currentQuestion?.score || 0} 分
                </span>
              </div>
              <div className="p-4">
                {(() => {
                  // 100% 复刻 quiz 页面的题干渲染逻辑
                  const imagePosition = (currentQuestion as any)?.image_position || 'below';
                  const currentLayout = (currentQuestion as any)?.title_image_layout || 'auto';
                  const hasTitleImages = currentQuestion?.title_images && currentQuestion.title_images.length > 0 && !currentQuestion?.content?.includes('<img');
                  const hasTitleContentImages = currentQuestion?.content?.includes('<img');
                  const isCodingQuestion = currentQuestion?.question_type === 'python_coding' ||
                                           currentQuestion?.question_type === 'cpp_coding' ||
                                           currentQuestion?.question_type === 'scratch_coding';

                  // 题目名称显示（已在上面的 header 中显示，这里不需要重复）

                  // 题干内容渲染
                  const titleText = currentQuestion?.content?.includes('<img') || currentQuestion?.content?.startsWith('<') ? (
                    <div className="prose prose-sm max-w-none">
                      <QuestionHtmlWithZoom
                        html={currentQuestion?.content || ''}
                        imageWidths={currentQuestion?.title_image_widths}
                      />
                    </div>
                  ) : (
                    <div
                      className="prose prose-sm max-w-none text-gray-700"
                      dangerouslySetInnerHTML={{ __html: processQuestionHtml(currentQuestion?.content || '') }}
                    />
                  );

                  // title_images 数组图片渲染
                  const titleImagesEl = hasTitleImages ? (() => {
                    const imageCount = currentQuestion.title_images?.length || 1;
                    // 自动布局：根据图片数量决定单行还是多行
                    const gridCols = imageCount <= 2 ? imageCount : 2;
                    return (
                      <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(${gridCols}, 1fr)` }}>
                        {currentQuestion.title_images?.map((imgUrl, idx) => {
                          const fullUrl = getImageUrl(imgUrl);
                          const savedWidth = currentQuestion.title_image_widths?.[idx];
                          const displayWidth = savedWidth != null ? savedWidth : 100;
                          return (
                            <div key={idx} className="relative overflow-hidden">
                              <img
                                src={fullUrl}
                                alt={`题目图片 ${idx + 1}`}
                                className="rounded-lg border border-gray-200"
                                style={{ width: '100%', maxWidth: '100%', height: 'auto', objectFit: 'contain' }}
                                loading="lazy"
                                onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                              />
                            </div>
                          );
                        })}
                      </div>
                    );
                  })() : null;

                  return (
                    <>
                      {imagePosition === 'above' && titleImagesEl}
                      {titleText}
                      {imagePosition === 'below' && titleImagesEl}
                    </>
                  );
                })()}
              </div>
            </motion.div>

            {/* 参考答案区 */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-green-50 rounded-xl border border-green-200 overflow-hidden"
            >
              <div className="px-4 py-3 bg-green-100 border-b border-green-200 flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-600" />
                <span className="font-medium text-green-700">参考答案</span>
              </div>
              <div className="p-4">
                {currentQuestion?.reference_answer ? (
                  <div
                    className="prose prose-sm max-w-none text-green-800"
                    dangerouslySetInnerHTML={{ __html: processQuestionHtml(currentQuestion.reference_answer) }}
                  />
                ) : currentQuestion?.correct_answer ? (
                  isCodeQuestion(currentQuestion.question_type) ? (
                    <div className="rounded-lg overflow-hidden border border-green-200">
                      <Editor
                        height="200px"
                        language={getLanguage(currentQuestion.question_type)}
                        value={currentQuestion.correct_answer}
                        options={{
                          readOnly: true,
                          minimap: { enabled: false },
                          scrollBeyondLastLine: false,
                          fontSize: 13,
                          lineNumbers: 'on',
                          theme: 'vs'
                        }}
                      />
                    </div>
                  ) : (
                    <div className="text-green-800 whitespace-pre-wrap">{currentQuestion.correct_answer}</div>
                  )
                ) : (
                  <div className="text-green-600 text-sm">暂无参考答案</div>
                )}
              </div>
            </motion.div>

            {/* 评分规则区 */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-blue-50 rounded-xl border border-blue-200 overflow-hidden"
            >
              <div className="px-4 py-3 bg-blue-100 border-b border-blue-200 flex items-center gap-2">
                <Award className="w-4 h-4 text-blue-600" />
                <span className="font-medium text-blue-700">评分规则</span>
              </div>
              <div className="p-4">
                {currentQuestion?.scoring_points ? (
                  <div
                    className="prose prose-sm max-w-none text-blue-800"
                    dangerouslySetInnerHTML={{ __html: processQuestionHtml(currentQuestion.scoring_points) }}
                  />
                ) : (
                  <div className="text-blue-700 text-sm space-y-1">
                    <p>评分标准：</p>
                    <ul className="list-disc list-inside">
                      <li>代码正确性：根据代码逻辑和运行结果评分</li>
                      <li>代码规范性：根据代码风格和注释评分</li>
                      <li>功能完整性：根据功能实现程度评分</li>
                    </ul>
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        </div>

        {/* 右栏 */}
        <div className="w-1/2 overflow-y-auto bg-gray-50">
          <div className="p-6 space-y-6">
            {/* 学生答案区 */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden"
            >
              <div className="px-4 py-3 bg-purple-50 border-b border-purple-100 flex items-center gap-2">
                <User className="w-4 h-4 text-purple-600" />
                <span className="font-medium text-purple-700">学生答案</span>
              </div>
              <div className="p-4">
                {!currentAnswer ? (
                  <div className="text-center py-8 text-gray-400">
                    <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>该题未作答</p>
                  </div>
                ) : isCodeQuestion(currentQuestion?.question_type || '') && currentAnswer.code_content ? (
                  <div className="rounded-lg overflow-hidden border border-gray-200">
                    <div className="bg-gray-50 px-3 py-2 border-b border-gray-200 flex items-center gap-2">
                      <Code className="w-4 h-4 text-gray-500" />
                      <span className="text-sm text-gray-600">学生代码</span>
                    </div>
                    <Editor
                      height="300px"
                      language={getLanguage(currentQuestion?.question_type || '')}
                      value={currentAnswer.code_content}
                      options={{
                        readOnly: true,
                        minimap: { enabled: false },
                        scrollBeyondLastLine: false,
                        fontSize: 13,
                        lineNumbers: 'on',
                        theme: 'vs'
                      }}
                    />
                  </div>
                ) : isScratchQuestion(currentQuestion?.question_type || '') && currentAnswer.file_url ? (
                  <div className="space-y-3">
                    <div className="flex items-center gap-3 p-3 bg-purple-50 rounded-lg border border-purple-100">
                      <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                        <Code className="w-5 h-5 text-purple-600" />
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-gray-800 text-sm">Scratch 项目</p>
                        <p className="text-xs text-gray-500">{currentAnswer.file_name || 'project.sb3'}</p>
                      </div>
                      <a
                        href={currentAnswer.file_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1 px-3 py-1.5 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700 transition-colors"
                      >
                        <Download className="w-3 h-3" />
                        下载
                      </a>
                    </div>
                  </div>
                ) : currentAnswer.answer_text ? (
                  <div className="text-gray-700 whitespace-pre-wrap">{currentAnswer.answer_text}</div>
                ) : currentAnswer.answer ? (
                  <div className="text-gray-700 whitespace-pre-wrap">{currentAnswer.answer}</div>
                ) : (
                  <div className="text-center py-8 text-gray-400">
                    <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>暂无作答数据</p>
                  </div>
                )}
              </div>
            </motion.div>

            {/* 评分区 */}
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-xl border-2 border-purple-200 shadow-sm overflow-hidden"
            >
              <div className="px-4 py-3 bg-purple-50 border-b border-purple-100 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Edit3 className="w-4 h-4 text-purple-600" />
                  <span className="font-medium text-purple-700">教师评分</span>
                </div>
                {draftSaved && (
                  <span className="text-xs text-green-600 flex items-center gap-1">
                    <Save className="w-3 h-3" />
                    草稿已保存 {lastSavedAt}
                  </span>
                )}
              </div>

              <div className="p-4 space-y-4">
                {/* AI评分参考 */}
                {examRecord.ai_score !== undefined && examRecord.ai_score !== null && (
                  <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
                    <div className="flex items-center gap-2 text-blue-700 text-sm mb-1">
                      <Bot className="w-4 h-4" />
                      <span className="font-medium">AI评分参考</span>
                    </div>
                    <div className="text-blue-800 font-semibold">
                      {examRecord.ai_score} / {currentQuestion?.score || examRecord.total_score} 分
                    </div>
                    {examRecord.ai_feedback && (
                      <div className="mt-2 text-blue-700 text-sm">{examRecord.ai_feedback}</div>
                    )}
                  </div>
                )}

                {/* 分数输入 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    评分（满分 {currentQuestion?.score || 0} 分）
                  </label>
                  <div className="flex items-center gap-3">
                    <input
                      type="number"
                      min={0}
                      max={currentQuestion?.score || 0}
                      value={currentForm.score}
                      onChange={(e) => updateEditForm(currentQuestion!.id, 'score', parseInt(e.target.value) || 0)}
                      className="flex-1 px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none transition-all"
                      placeholder="请输入分数"
                    />
                    <span className="text-gray-500 font-medium">/ {currentQuestion?.score || 0}</span>
                  </div>
                </div>

                {/* 评语输入 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    评语
                  </label>
                  <textarea
                    value={currentForm.feedback}
                    onChange={(e) => updateEditForm(currentQuestion!.id, 'feedback', e.target.value)}
                    rows={4}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none transition-all resize-none"
                    placeholder="请输入评语..."
                  />
                </div>

                {/* 操作按钮 */}
                <div className="flex items-center justify-between pt-2">
                  <button
                    onClick={() => {
                      if (examRecord.ai_score !== undefined && examRecord.ai_score !== null) {
                        updateEditForm(currentQuestion!.id, 'score', examRecord.ai_score);
                        if (examRecord.ai_feedback) {
                          updateEditForm(currentQuestion!.id, 'feedback', examRecord.ai_feedback);
                        }
                      }
                    }}
                    disabled={examRecord.ai_score === undefined || examRecord.ai_score === null}
                    className="flex items-center gap-1.5 px-4 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <RotateCcw className="w-4 h-4" />
                    使用AI评分
                  </button>

                  <button
                    onClick={handleSave}
                    disabled={saving || isCurrentQuestionSaved}
                    className="flex items-center gap-2 px-6 py-2.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {saving ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        保存中...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4" />
                        确认评分
                      </>
                    )}
                  </button>
                </div>

                {/* 提示 */}
                <div className="p-3 bg-amber-50 rounded-lg border border-amber-100">
                  <p className="text-sm text-amber-700">
                    <strong>提示：</strong>评分将自动保存为草稿，每30秒自动备份。点击"确认评分"后提交最终成绩。
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
}